import React, { useState, useEffect, useRef } from 'react';
import axios from '../../../services/api';
import { useTranslation } from 'react-i18next';
import CallModal from './CallModal';
import ConfirmCallModal from './ConfirmCallModal';
import ConfirmModal from './ConfirmModal';
import { toast } from 'react-toastify';

const parseIceServers = (serverString) => {
  if (!serverString || typeof serverString !== 'string') return [];
  const servers = serverString.split(',').map(s => s.trim()).filter(Boolean);
  const iceServers = [];
  const turnUsername = process.env.REACT_APP_TURN_USERNAME || '';
  const turnPassword = process.env.REACT_APP_TURN_PASSWORD || '';
  console.log({ turnUsername });
  console.log({ turnPassword });
  for (const server of servers) {
    if (/^turn:/i.test(server)) {
      iceServers.push({ urls: server, username: turnUsername, credential: turnPassword });
    } else if (/^stun:/i.test(server)) {
      iceServers.push({ urls: server });
    }
  }
  return iceServers;
};

const getTimestamp = () => {
  const now = new Date();
  return now.toLocaleTimeString('zh-TW', { hour12: false }) + '.' + String(now.getMilliseconds()).padStart(3, '0');
};

const createTimer = (label) => {
  const start = Date.now();
  return {
    mark: (subLabel) => {
      const elapsed = Date.now() - start;
      console.log(`⏱️ [性能] ${label}${subLabel ? ' - ' + subLabel : ''} | 耗時: ${elapsed}ms`);
    },
    end: (subLabel) => {
      const elapsed = Date.now() - start;
      console.log(`✅ [性能] ${label}${subLabel ? ' - ' + subLabel : ''} | 總耗時: ${elapsed}ms`);
      return elapsed;
    }
  };
};

const MainUI = ({ type, lockId, ua }) => {
  const { t } = useTranslation();
  const [buildings, setBuildings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isConfirmCallModalOpen, setIsConfirmCallModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [callStatus, setCallStatus] = useState('');
  const [isVideoCall, setIsVideoCall] = useState(false);
  const [isLocalVideoActive, setIsLocalVideoActive] = useState(false);
  const [isSystemReady, setIsSystemReady] = useState(false);

  const sessionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const iceServersRef = useRef([]);
  const tempPcRef = useRef(null);
  const candidateBufferRef = useRef([]);

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await axios.get(`/visitor/buildings`, {
          params: { type, lockId },
        });
        setBuildings(response.data);
        // If type is 02, auto-select the single building
        if (type === '02' && response.data.length > 0) {
          setSelectedBuilding(response.data[0]);
          fetchRooms(response.data[0]._id);
        }
      } catch (error) {
        console.error('Error fetching buildings:', error);
      }
    };
    fetchBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, lockId]);

  const applyCodecPreferences = (pc) => {
    if (!pc || !RTCRtpSender.getCapabilities) return;
    const audioTransceiver = pc.getTransceivers().find(t =>
      (t.sender?.track?.kind === 'audio' || t.receiver?.track?.kind === 'audio')
    );
    if (audioTransceiver) {
      const audioCaps = RTCRtpSender.getCapabilities('audio');
      const pcmuCodec = audioCaps.codecs.find(c => c.mimeType.toLowerCase() === 'audio/pcmu');
      const dtmfCodec = audioCaps.codecs.find(c => c.mimeType.toLowerCase().includes('telephone-event'));
      if (pcmuCodec && dtmfCodec) {
        try { audioTransceiver.setCodecPreferences([pcmuCodec, dtmfCodec]); } catch(e) {console.log(e);}
      }
    }
  };

  const bindRemoteAudio = (pc) => {
    if (!pc) return;
    try {
      const audioReceiver = pc.getReceivers().find(r => r.track && r.track.kind === 'audio');
      if (!audioReceiver?.track) return;
      const audioEl = document.getElementById('remoteAudio');
      if (!audioEl) return;
      const stream = new MediaStream([audioReceiver.track]);
      audioEl.srcObject = stream;
      audioEl.play().catch(() => {});
    } catch (e) { console.error('音頻喚醒失敗:', e); }
  };

  const upgradeToVideo = async () => {
    const session = sessionRef.current;
    if (!session || session.connection.signalingState !== 'stable') {
      setTimeout(upgradeToVideo, 500);
      return;
    }
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, frameRate: 20 }
      });
      const pc = session.connection;
      const videoTrack = videoStream.getVideoTracks()[0];
      if (localStreamRef.current) localStreamRef.current.addTrack(videoTrack);
      pc.addTrack(videoTrack, localStreamRef.current || videoStream);
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
      setIsLocalVideoActive(true);
      applyCodecPreferences(pc);
      console.log('📹 [視頻] 視頻升級完成 | 時間:' + getTimestamp());
      setTimeout(() => {
        session.renegotiate({
          useAudio: true, useVideo: true,
          rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: true }
        });
      }, 500);
      setTimeout(() => bindRemoteAudio(pc), 3500);
    } catch (e) {
      console.log('❌ [視頻] 視頻升級失敗:', e.message, '| 時間:' + getTimestamp());
      toast.error('無法開啟攝像頭');
    }
  };

  const fetchRooms = async buildingId => {
    try {
      const response = await axios.get(`/visitor/rooms`, {
        params: { buildingId },
      });
      const sortedRooms = response.data.sort((a, b) => {
        const roomA = (a?.roomNumber || '').toString().toLowerCase();
        const roomB = (b?.roomNumber || '').toString().toLowerCase();

        if (!roomA && roomB) return 1;
        if (roomA && !roomB) return -1;
        if (!roomA && !roomB) return 0;

        return roomA.localeCompare(roomB, undefined, { numeric: true, sensitivity: 'base' });
      });
      setRooms(sortedRooms);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleBuildingClick = building => {
    setSelectedBuilding(building);
    fetchRooms(building._id);
  };

  const handleConfirm = room => {
    if (!ua) {
      toast.error(t('Please connect to the network'));
      return;
    }
    setSelectedRoom(room);
    setIsConfirmCallModalOpen(true);
  };

  const handleCallClick = async (video) => {
    console.log('=== 🔵 [UI] 用戶點擊通話按鈕 ===');
    console.log('🕐 時間:', getTimestamp());
    console.log('📹 通話類型:', video ? '視頻' : '語音');

    console.log('🔍 [ICE] ICE 服務器配置:', JSON.stringify(iceServersRef.current));
    console.log('🔍 [ICE] 環境變數 REACT_APP_ICE_SERVERS:', process.env.REACT_APP_ICE_SERVERS);
    console.log('🔍 [ICE] 環境變數 REACT_APP_TURN_USERNAME:', process.env.REACT_APP_TURN_USERNAME ? '已設置' : '未設置');
    console.log('🔍 [ICE] 環境變數 REACT_APP_TURN_PASSWORD:', process.env.REACT_APP_TURN_PASSWORD ? '已設置' : '未設置');
    if (iceServersRef.current.length === 0) {
      console.warn('⚠️ [ICE] 警告：ICE 服務器列表為空，請檢查環境變數 REACT_APP_ICE_SERVERS 配置');
    } else {
      console.log('✅ [ICE] ICE 服務器列表正常，共', iceServersRef.current.length, '個服務器');
    }

    setIsConfirmCallModalOpen(false);

    if (!ua) {
      toast.error(t('Please connect to the network'));
      return;
    }

    setIsVideoCall(video);
    setIsSystemReady(false);
    candidateBufferRef.current = [];
    console.log('🔄 [狀態] 初始化完成 | 時間:' + getTimestamp());

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') ctx.resume();
      console.log('🎵 [音頻] AudioContext 已解鎖 | 時間:' + getTimestamp());
    } catch (e) {
      console.log(e);
    }

    try {
      setIsConfirmOpen(true);
      console.log('📋 [UI] 顯示確認框 | 時間:' + getTimestamp());

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      console.log('✅ [媒體] 麥克風權限已授予 | 時間:' + getTimestamp());

      const tempPc = new RTCPeerConnection({ iceServers: iceServersRef.current });
      tempPcRef.current = tempPc;
      stream.getTracks().forEach(track => tempPc.addTrack(track, stream));
      console.log('🔌 [ICE] 臨時 PeerConnection 已創建 | 時間:' + getTimestamp());

      let hasRelay = false;
      const hardTimeout = setTimeout(() => {
        if (!isSystemReady) {
          console.warn('⚠️ [ICE] 硬性超時，強制開鎖 | 時間:' + getTimestamp());
          setIsSystemReady(true);
        }
      }, 3000);

      tempPc.onicecandidate = (event) => {
        if (event.candidate) {
          candidateBufferRef.current.push(event.candidate);
          console.log('🔍 [ICE] 探測到路徑:', event.candidate.candidate.substring(0, 100) + '... | 時間:' + getTimestamp());
          if (event.candidate.candidate.includes('typ relay') && !hasRelay) {
            hasRelay = true;
            console.log('🟢 [ICE] 成功獲取 Relay 路徑，系統就緒 | 時間:' + getTimestamp());
            clearTimeout(hardTimeout);
            setIsSystemReady(true);
          }
        }
      };

      const offer = await tempPc.createOffer();
      await tempPc.setLocalDescription(offer);
      console.log('📝 [ICE] Offer 已創建並設置 | 時間:' + getTimestamp());

    } catch (e) {
      console.log('❌ [媒體] 權限獲取失敗:', e.message, '| 時間:' + getTimestamp());
      toast.error('請授權麥克風');
      setIsConfirmOpen(false);
    }
  };

  const handleHangup = () => {
    console.log('📞 [通話] 執行掛斷 | 時間:' + getTimestamp());

    if (window._iceReadyTimer) {
      clearTimeout(window._iceReadyTimer);
      window._iceReadyTimer = null;
      console.log('🧹 [ICE] 清理 iceReadyTimer');
    }

    if (sessionRef.current?.connection) {
      const pc = sessionRef.current.connection;
      if (pc._keyFrameInterval) {
        clearInterval(pc._keyFrameInterval);
        pc._keyFrameInterval = null;
        console.log('✅ [KeyFrame] Key frame interval cleared');
      }
    }

    localStreamRef.current?.getTracks().forEach(track => track.stop());
    if (tempPcRef.current) { tempPcRef.current.close(); tempPcRef.current = null; }
    if (sessionRef.current) { try { sessionRef.current.terminate(); } catch (e) {console.log(e);} }
    setIsCallModalOpen(false);
    setIsConfirmOpen(false);
    setIsLocalVideoActive(false);
    sessionRef.current = null;
    candidateBufferRef.current = [];
    console.log('✅ [通話] 掛斷完成 | 時間:' + getTimestamp());
  };

  const handleToggleVideo = () => {
    const videoTrack = localStreamRef.current?.getVideoTracks()[0];
    if (videoTrack) {
      const newState = !videoTrack.enabled;
      videoTrack.enabled = newState;
      setIsLocalVideoActive(newState);
      console.log('📹 [視頻] 切換狀態:', newState ? '開啟' : '關閉', '| 時間:' + getTimestamp());
    }
  };

  const handleConfirmCall = () => {
    const callTimer = createTimer('handleConfirmCall');
    const callStartTime = Date.now();

    console.log('=== 🔴 [UI] 用戶點擊確認呼叫 ===');
    console.log('🕐 時間:', getTimestamp());
    console.log('⏱️ [性能] 時間戳:', callStartTime);

    console.log('🔍 [DEBUG] ========== selectedRoom 診斷 ==========');
    console.log('🔍 [DEBUG] selectedRoom:', selectedRoom);
    console.log('🔍 [DEBUG] selectedRoom.sipAccount:', selectedRoom?.sipAccount);
    console.log('🔍 [DEBUG] selectedRoom.sipAccount 類型:', typeof selectedRoom?.sipAccount);
    console.log('🔍 [DEBUG] selectedRoom.sipAccount 長度:', selectedRoom?.sipAccount?.length);

    const targetUri = `sip:${selectedRoom?.sipAccount}@sip.qrtalkie.org`;
    console.log('🔍 [DEBUG] 構建的目標 URI:', targetUri);

    const uriRegex = /^sip:[^@]+@[^@]+$/;
    console.log('🔍 [DEBUG] URI 格式驗證:', uriRegex.test(targetUri) ? '✅ 正確' : '❌ 錯誤');
    console.log('🔍 [DEBUG] ========== 診斷結束 ==========');

    console.log('📞 被叫號碼:', targetUri);
    console.log('📹 通話類型:', isVideoCall ? '視頻' : '語音');

    console.log('🔍 [DEBUG] isSystemReady:', isSystemReady);
    console.log('🔍 [DEBUG] candidateBufferRef 長度:', candidateBufferRef.current.length);
    console.log('🔍 [DEBUG] ua 狀態:', ua ? '已初始化' : '未初始化');
    console.log('🔍 [DEBUG] selectedRoom:', selectedRoom ? '已加載' : '未加載');
    console.log('🔍 [DEBUG] localStream:', localStreamRef.current ? '已獲取' : '未獲取');
    callTimer.mark('狀態檢查完成');

    const transport = ua?.transport;
    const socket = transport?.socket;
    console.log('🔌 [DEBUG] WebSocket readyState:', socket?.readyState);
    console.log('🔌 [DEBUG] WebSocket URL:', socket?.url);
    console.log('🔌 [DEBUG] transport 狀態:', transport?.connected);
    console.log('🔌 [DEBUG] WebSocket bufferedAmount:', socket?.bufferedAmount);
    callTimer.mark('WebSocket 狀態檢查');

    setIsConfirmCallModalOpen(false);
    setIsCallModalOpen(true);
    setCallStatus('呼叫中...');

    const audioEl = document.getElementById('remoteAudio');
    if (audioEl) audioEl.play().catch(() => {});

    console.log('=== 📤 [SIP] 即將發送 INVITE 請求 ===');
    console.log('🕐 時間:', getTimestamp());
    callTimer.mark('UI 狀態更新完成');

    const options = {
      mediaStream: localStreamRef.current,
      pcConfig: {
        iceServers: iceServersRef.current,
        iceTransportPolicy: 'all',
        iceCandidatePoolSize: 0
      },
      eventHandlers: {
        icecandidate: (data) => {
          const { candidate, ready } = data;

          if (candidate) {
            console.log(`🔌 [ICE] candidate: ${candidate.type} | ${candidate.candidate?.substring(0, 100)}... | 時間: ${getTimestamp()}`);
          }

          if (candidate?.candidate?.includes('typ relay')) {
            console.log('✅ [ICE] relay 候選已收集，調用 ready() 立即發送 INVITE | 時間:', getTimestamp());
            ready();
            return;
          }

          if (!window._iceReadyTimer) {
            window._iceReadyTimer = setTimeout(() => {
              console.log('⏰ [ICE] 1 秒超時，強制調用 ready() 發送 INVITE | 時間:', getTimestamp());
              if (ready) ready();
              window._iceReadyTimer = null;
            }, 1000);
          }
        },

        connecting: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('📤 [SIP] connecting 事件觸發 | 耗時:', elapsed, 'ms');
          console.log('📤 [SIP] 正在發送 INVITE | 耗時:', elapsed, 'ms');
          console.log('📞 [SIP] 請求 URI (to_uri):', data.request?.to_uri?.toString());
          console.log('📞 [SIP] 請求 URI (ruri):', data.request?.ruri?.toString());
          console.log('📞 [SIP] Call-ID:', data.request?.call_id);
          console.log('🔍 [SIP] request 對象:', data.request);
          callTimer.mark('connecting 事件觸發');
        },

        progress: (data) => {
          const elapsed = Date.now() - callStartTime;
          const response = data.response;
          console.log('📥 [SIP] 收到響應:', response.status_code, response.reason_phrase, '| 耗時:', elapsed, 'ms');
          console.log('📥 [SIP] From Tag:', response.from_tag);
          console.log('📥 [SIP] To Tag:', response.to_tag);
          console.log('📥 [SIP] Call-ID:', response.call_id);

          if (response.body) {
            console.log('📥 [SIP] 包含 SDP:', response.body.length, 'bytes');
          }

          setCallStatus('正在響鈴...');
          callTimer.mark('progress 事件觸發');

          const pc = sessionRef.current?.connection;
          if (pc && candidateBufferRef.current.length > 0) {
            console.log('🔌 [ICE] 注入緩存候選 | 數量:', candidateBufferRef.current.length);
            candidateBufferRef.current.forEach(cand => {
              pc.addIceCandidate(cand).catch(err => {
                if (err.name !== 'InvalidStateError') {
                  console.log('⚠️ [ICE] 注入候選失敗:', err.message);
                }
              });
            });
            candidateBufferRef.current = [];
          }
        },

        ringing: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('📥 [SIP] 收到 180 Ringing | 耗時:', elapsed, 'ms');
          console.log('📥 [SIP] From Tag:', data.response?.from_tag);
          console.log('📥 [SIP] To Tag:', data.response?.to_tag);
          setCallStatus('正在響鈴...');
          callTimer.mark('ringing 事件觸發');
        },

        peerconnection: (e) => {
          const elapsed = Date.now() - callStartTime;
          console.log('🔌 [Event] peerconnection | ICE 開始協商 | 耗時:', elapsed, 'ms');
          callTimer.mark('peerconnection 事件觸發');

          const pc = e.peerconnection;

          console.log('🔍 [ICE] PC 初始狀態:');
          console.log('  - signalingState:', pc.signalingState);
          console.log('  - iceGatheringState:', pc.iceGatheringState);
          console.log('  - iceConnectionState:', pc.iceConnectionState);
          console.log('  - localDescription:', pc.localDescription ? '已設置' : '未設置');
          console.log('  - remoteDescription:', pc.remoteDescription ? '已設置' : '未設置');

          applyCodecPreferences(pc);

          const setupKeyFrameHandling = () => {
            try {
              const senders = pc.getSenders();
              const videoSenders = senders.filter(s => s.track && s.track.kind === 'video');

              if (videoSenders.length === 0) {
                console.log('⚠️ [KeyFrame] No video senders found, will retry...');
                setTimeout(setupKeyFrameHandling, 1000);
                return;
              }

              console.log(`✅ [KeyFrame] Found ${videoSenders.length} video sender(s)`);

              const requestKeyFrame = async () => {
                try {
                  for (const sender of videoSenders) {
                    try {
                      const params = await sender.getParameters();
                      if (!params.encodings || params.encodings.length === 0) {
                        params.encodings = [{}];
                      }

                      params.encodings.forEach(encoding => {
                        encoding.scaleResolutionDownBy = encoding.scaleResolutionDownBy === 1.0 ? 1.01 : 1.0;
                      });

                      await sender.setParameters(params);
                      // console.log('🔄 [KeyFrame] Key frame requested via setParameters');

                      setTimeout(async () => {
                        try {
                          const resetParams = await sender.getParameters();
                          resetParams.encodings.forEach(encoding => {
                            encoding.scaleResolutionDownBy = 1.0;
                          });
                          await sender.setParameters(resetParams);
                        } catch (e) {
                          // Ignore reset errors
                        }
                      }, 100);

                    } catch (err) {
                      console.warn('⚠️ [KeyFrame] Failed to request key frame:', err);
                    }
                  }
                } catch (err) {
                  console.error('❌ [KeyFrame] Error requesting key frame:', err);
                }
              };

              const keyFrameInterval = setInterval(() => {
                if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
                  clearInterval(keyFrameInterval);
                  return;
                }

                requestKeyFrame();
              }, 100);

              pc._keyFrameInterval = keyFrameInterval;

              console.log('✅ [KeyFrame] Periodic key frame requests enabled (every 2s)');

            } catch (err) {
              console.error('❌ [KeyFrame] Error setting up key frame handling:', err);
            }
          };

          pc.onicegatheringstatechange = () => {
            console.log('🔍 [ICE] gatheringState 變更:', pc.iceGatheringState, '| 時間:', getTimestamp());
            if (pc.iceGatheringState === 'complete') {
              console.log('✅ [ICE] ICE 收集完成');
            }
          };

          pc.oniceconnectionstatechange = () => {
            console.log('🔌 [ICE] 狀態變更:', pc.iceConnectionState, '| 時間:', getTimestamp());
            if (pc.iceConnectionState === 'connected') {
              console.log('✅ [ICE] 連接已建立');
              bindRemoteAudio(pc);
              setTimeout(setupKeyFrameHandling, 1000);
            }
          };

          if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
            setTimeout(setupKeyFrameHandling, 1000);
          }

          pc.onicecandidate = (event) => {
            if (event.candidate) {
              console.log('🔍 [ICE] 正式 PC 收集到候選:', event.candidate.type, '| 時間:', getTimestamp());
            } else {
              console.log('✅ [ICE] 正式 PC 候選收集結束 | 時間:', getTimestamp());
            }
          };

          pc.ontrack = (event) => {
            console.log('🎵 [Event] 收到軌道 | 類型:', event.track.kind, '| 時間:', getTimestamp());
            if (event.track.kind === 'video' && remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = new MediaStream([event.track]);
            }
          };
        },

        accepted: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('✅ [SIP] 收到 200 OK | 耗時:', elapsed, 'ms');
          console.log('✅ [SIP] From Tag:', data.response?.from_tag);
          console.log('✅ [SIP] To Tag:', data.response?.to_tag);
          console.log('✅ [SIP] Contact:', data.response?.contact?.[0]?.uri?.toString());

          if (data.response?.body) {
            console.log('✅ [SIP] SDP 長度:', data.response.body.length, 'bytes');
          }
          callTimer.mark('accepted 事件觸發');
        },

        confirmed: (_) => {
          const elapsed = Date.now() - callStartTime;
          console.log('✅ [Event] confirmed | 通話已建立 | 總耗時:', elapsed, 'ms');
          setCallStatus('通話中');
          callTimer.mark('confirmed 事件觸發');

          const pc = sessionRef.current?.connection;
          if (pc && candidateBufferRef.current.length > 0) {
            console.log('🔌 [ICE] 注入剩餘候選 | 數量:', candidateBufferRef.current.length);
            candidateBufferRef.current.forEach(cand => {
              pc.addIceCandidate(cand).catch(err => {
                if (err.name !== 'InvalidStateError') {
                  console.log('⚠️ [ICE] 注入候選失敗:', err.message);
                }
              });
            });
            candidateBufferRef.current = [];
          }

          if (isVideoCall) setTimeout(upgradeToVideo, 4500);
        },

        failed: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('❌ [SIP] 呼叫失敗 | 耗時:', elapsed, 'ms');
          console.log('❌ [SIP] 失敗原因:', data.cause?.code, data.cause?.reason);
          console.log('❌ [SIP] 失敗詳情:', data);

          if (data.response) {
            console.log('❌ [SIP] 響應碼:', data.response.status_code, data.response.reason_phrase);
          }

          toast.error('呼叫失敗: ' + (data.cause?.reason || '未知錯誤'));
          handleHangup();
          callTimer.mark('failed 事件觸發');
        },

        ended: (data) => {
          const elapsed = Date.now() - callStartTime;
          console.log('📞 [Event] ended | 通話結束 | 總時長:', elapsed, 'ms');
          console.log('📞 [SIP] 結束原因:', data.cause?.code, data.cause?.reason);
          handleHangup();
          callTimer.mark('ended 事件觸發');
        },

        newRTCSession: (data) => {
          console.log('📞 [SIP] 新 RTC 會話 | 方向:', data.originator);
          console.log('📞 [SIP] 會話 ID:', data.session?.id);
          console.log('📞 [SIP] session 對象:', data.session);
          callTimer.mark('newRTCSession 事件觸發');
        }
      }
    };

    console.log('📤 [SIP] 執行 ua.call() 前 | 時間:', getTimestamp());
    callTimer.mark('準備調用 ua.call()');

    console.log('🔍 [DEBUG] ua.call() 參數診斷:');
    console.log('  - target URI:', targetUri);
    console.log('  - options.mediaStream:', options.mediaStream ? '已設置' : '未設置');
    console.log('  - options.pcConfig.iceServers:', options.pcConfig.iceServers);
    console.log('  - options.pcConfig.iceCandidatePoolSize:', options.pcConfig.iceCandidatePoolSize);

    const callExecStart = Date.now();
    console.log('⏱️ [DEBUG] ua.call() 開始執行');

    try {
      sessionRef.current = ua.call(targetUri, options);
      const callExecEnd = Date.now();
      console.log('✅ [SIP] ua.call() 執行完成 | 耗時:', callExecEnd - callExecStart, 'ms');
      console.log('✅ [SIP] sessionRef.current:', sessionRef.current ? '已創建' : 'null');
      console.log('✅ [SIP] session 對象:', sessionRef.current);

      if (sessionRef.current) {
        console.log('🔍 [SIP] session.connection:', sessionRef.current.connection);
        console.log('🔍 [SIP] session.request:', sessionRef.current.request);
      }
    } catch (error) {
      console.error('❌ [SIP] ua.call() 執行異常:', error);
      console.error('❌ [SIP] 錯誤堆棧:', error.stack);
    }

    callTimer.mark('ua.call() 執行完成');

    if (tempPcRef.current) {
      tempPcRef.current.close();
      tempPcRef.current = null;
      console.log('🔌 [ICE] 臨時 PC 已關閉');
    }

    console.log('✅ [SIP] INVITE 已發送 | 時間:', getTimestamp());
    callTimer.end('handleConfirmCall 總耗時');
  };

  useEffect(() => {
    iceServersRef.current = parseIceServers(process.env.REACT_APP_ICE_SERVERS);
    console.log('🔍 [ICE] parseIceServers 解析結果:', JSON.stringify(iceServersRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex" style={{ height: 'calc(100vh - 50px)' }}>
      {/* Left Panel - Buildings */}
      <div className="w-1/2 border-r overflow-y-auto h-full py-2">
        <div className="px-2 space-y-2">
          {buildings.map(building => (
            <div
              key={building.buildingId}
              className={`building-card ${
                selectedBuilding?.buildingId === building.buildingId ? 'hover' : ''
              }`}
              onClick={() => handleBuildingClick(building)}
            >
              {building.buildingName}
            </div>
          ))}
        </div>
      </div>

      {/* Right Panel - Rooms */}
      <div className="w-1/2 overflow-y-auto h-full py-2">
        <div className="px-2 space-y-2">
          {rooms.map(room => (
            <div
              key={room._id}
              className="building-card"
              onClick={() => {
                handleConfirm(room);
              }}
            >
              {room.roomNumber}
            </div>
          ))}
        </div>
      </div>

      <ConfirmCallModal
        isOpen={isConfirmCallModalOpen}
        onClose={() => {
          setIsConfirmCallModalOpen(false);
        }}
        onConfirmVoice={() => {
          handleCallClick(false);
        }}
        onConfirmVideo={() => {
          handleCallClick(true);
        }}
        title={t('Call Room')}
        // message={`${t('Are you sure you want to call this number room?', { number: selectedRoom?.roomNumber })} test`}
        message={
          <>
            {t('Are you sure you want to call this number room?', {
              number: selectedRoom?.roomNumber ?? '',
            })}
            <br />
            {t('SIP Account')} :{' '}
            {`sip:${selectedRoom?.sipAccount}@${selectedRoom?.sipAccountInfo?.domain ?? 'im.qrtalkie.com'}`}
          </>
        }
        confirmVoiceText="voice_call"
        confirmVideoText="video_call"
        cancelText="Cancel"
        type="info"
      />

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmCall}
        title={t('Initiate Call')}
        type="info"
        confirmText={isSystemReady ? t('Confirm') : t('Network optimizing...')}
        confirmDisabled={!isSystemReady}
        cancelText={t('Cancel')}
        message={t('Are you sure you want to call this number room?', { number: selectedRoom?.roomNumber ?? '' })}
      />

      <CallModal
        ref={{ remoteVideoRef, localVideoRef }}
        isOpen={isCallModalOpen}
        onClose={handleHangup}
        onToggleVideo={handleToggleVideo}
        callStatus={callStatus}
        isVideoCall={isVideoCall}
        isLocalVideoActive={isLocalVideoActive}
        roomName={selectedRoom?.roomNumber}
        onOpenStats={() => {}}
      />

      <audio id="remoteAudio" autoPlay />
    </div>
  );
};

export default MainUI;
